import { generateUUID } from '../../utils/common/uuid';
import { useCallback, useRef } from 'react';
import { restoreLegacyCollapsedMap } from '../../utils/mindmap/branchVisibility';
import { Parent } from '../../models/Parent';
import { RelativePosition } from '../../models/RelativePosition';
import { COLORS } from '../../config/colors';
//import { saveMyMindMap, saveMindMapById } from '../../utils/mindmap/apiClient';

const ensureNodePosition = (node, containerRef) => {
  if (!node || (node.position && typeof node.position.x === 'number' && typeof node.position.y === 'number')) {
    return node;
  }

  const containerRect = containerRef?.current?.getBoundingClientRect?.();
  const x = containerRect ? containerRect.width / 2 - 100 : 300;
  const y = containerRect ? containerRect.height / 2 : 300;

  return {
    ...node,
    position: {
      x,
      y,
    },
  };
};

const normalizeNodes = (nodesArray, containerRef) => {
  if (!Array.isArray(nodesArray)) return [];
  let changed = false;
  const normalized = nodesArray.map((node) => {
    const next = ensureNodePosition(node, containerRef);
    if (next !== node) changed = true;
    return next;
  });
  return changed ? normalized : nodesArray;
};

const hasLegacyCollapsedContent = (nodes) => (nodes || []).some((node) => (
  Array.isArray(node?.collapsedChildren?.nodes)
  && node.collapsedChildren.nodes.length > 0
));

const rebuildParentMap = (parentMap, nodes, relationships) => {
  const nodesById = new Map((nodes || []).map((node) => [node?.id, node]));
  const childrenByParent = new Map();
  (relationships || []).forEach((relationship) => {
    if (!relationship?.parentId || !relationship?.childId) return;
    const children = childrenByParent.get(relationship.parentId) || [];
    children.push(relationship.childId);
    childrenByParent.set(relationship.parentId, children);
  });

  const next = new Map();
  nodesById.forEach((node, nodeId) => {
    if (!node?.position) return;
    const parent = new Parent(node.position, nodeId);
    parent.children = (childrenByParent.get(nodeId) || []).map((childId) => {
      const child = nodesById.get(childId);
      if (!child?.position) return null;
      return {
        id: childId,
        relativePosition: RelativePosition.fromPoint(
          node.position.x,
          node.position.y,
          child.position.x,
          child.position.y
        ),
      };
    }).filter(Boolean);
    next.set(nodeId, parent);
  });
  parentMap.current = next;
};

/**
 * Custom hook for saving and loading mind map data
 * Centralizes all persistence logic and eliminates data structure duplication
 */
export const usePersistence = ({ nodeManagement }) => {
  const {
    setNodes,
    setRelationships,
    setSelectedId,
    setRoot,
    parentMap
  } = nodeManagement;
  const projectedRelationshipsRef = useRef(null);

  const getDefaultData = useCallback((containerRef) => {
    const id = generateUUID();
    const containerRect = containerRef?.current?.getBoundingClientRect();
    const position = {
      x: containerRect ? containerRect.width / 2 - 100 : 300,
      y: containerRect ? containerRect.height / 2 : 300
    };

    return {
      nodes: [{
        id,
        text: "",
        position,
        color: COLORS[0].gradient,
        isRoot: true
      }],
      relationships: [],
      root: id,
      selectedId: id
    };
  }, []);

  const isValidData = useCallback((data) => {
    if (!data || typeof data !== 'object') return false;
    if (!Array.isArray(data.nodes) || !Array.isArray(data.relationships)) return false;
    return true;
  }, []);

  // For the signed-in-only flow, auth context is simple: we either
  // have a token (and thus an ownerUserId) or we don't render the
  // mind map at all. This hook is only used from within the signed-in
  // experience, so we can assume a token is present when mounted.
  const getAuthContext = useCallback(() => {
    if (typeof window === 'undefined') {
      return { hasAuthToken: false, ownerUserId: null };
    }

    let ownerUserId = null;
    let hasAuthToken = false;

    try {
      const token = window.localStorage.getItem('mindmappi_auth_token');
      hasAuthToken = !!token;
    } catch {
      hasAuthToken = false;
    }

    try {
      const metaRaw = window.localStorage.getItem('mindMapAuthMeta');
      if (metaRaw) {
        const meta = JSON.parse(metaRaw);
        if (meta && typeof meta === 'object' && meta.ownerUserId) {
          ownerUserId = meta.ownerUserId;
        }
      }
    } catch {
      ownerUserId = null;
    }

    return { hasAuthToken, ownerUserId };
  }, []);

  /*
  const buildStorageKey = useCallback((ownerUserId, mindmapId) => {
    if (!mindmapId) return null;
    const prefix = ownerUserId ? `mindMap_${ownerUserId}` : 'mindMap_guest';
    return `${prefix}_${mindmapId}`;
  }, []);

  const readIndex = useCallback((ownerUserId) => {
    if (typeof window === 'undefined') return [];
    const key = ownerUserId ? `mindMapIndex_${ownerUserId}` : 'mindMapIndex_guest';
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const writeIndex = useCallback((ownerUserId, index) => {
    if (typeof window === 'undefined') return;
    const key = ownerUserId ? `mindMapIndex_${ownerUserId}` : 'mindMapIndex_guest';
    try {
      window.localStorage.setItem(key, JSON.stringify(index));
    } catch (err) {
      console.error('Failed to write mind map index:', err);
    }
  }, []);
  */

  const serializeMap = useCallback(() => {
    // Add safety checks to prevent errors when nodeManagement is updating
    if (!nodeManagement || !nodeManagement.nodes || !nodeManagement.relationships) {
      console.warn('nodeManagement not ready for serialization');
      return {
        nodes: [],
        relationships: [],
        root: null,
        selectedId: null,
      };
    }

    return {
      nodes: JSON.parse(JSON.stringify(nodeManagement.nodes)),
      relationships: JSON.parse(JSON.stringify(nodeManagement.relationships)),
      root: nodeManagement.root,
      selectedId: nodeManagement.selectedId,
    };
  }, [nodeManagement]);

  // Optional `snapshotOverride` lets callers provide an explicit
  // state snapshot (nodes/relationships/root/selectedId) to persist,
  // avoiding timing issues with React state updates.
  const saveMindMap = useCallback(async (snapshotOverride) => {
    // Persist the current structural snapshot to the backend for the
    // signed-in user. Structural state itself is owned by Yjs; this
    // save is best-effort for export/backup and for the sign-out flow.
    //const base = snapshotOverride || serializeMap();

    let mindmapId = null;
    let ownerUserId = null;

    const { hasAuthToken, ownerUserId: authOwnerUserId } = getAuthContext();

    // If we're currently viewing a specific map (via ?map=),
    // treat that as the authoritative id for this save
    let urlMapId = null;
    try {
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        const mapParam = url.searchParams.get('map');
        if (mapParam) {
          urlMapId = mapParam;
        }
      }
    } catch {
      urlMapId = null;
    }

    if (urlMapId) {
      mindmapId = urlMapId;
      ownerUserId = authOwnerUserId || null;
    } else {
      // When saving the primary map (no ?map= in URL), do NOT use
      // the stored mindmapId because it might be from a different
      // linked map that was previously edited in this session.
      // Always let the backend determine the primary map ID.
      mindmapId = null;
      ownerUserId = authOwnerUserId || null;
    }

    if (!hasAuthToken || !ownerUserId) {
      // Without a token / owner we have no business saving anything.
      return;
    }

    //const data = { ...base, mindmapId, ownerUserId };

    // In the new flow, structural persistence for signed-in users is
    // handled by the backend/Yjs. This hook can be wired to a REST
    // endpoint (e.g. saveMyMindMap) if needed, but we intentionally
    // avoid any localStorage-based structural persistence.
    if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.log('[DEBUG] Mind map save requested - hasAuthToken:', hasAuthToken, 'mindmapId:', mindmapId);
    }
  }, [ getAuthContext]);

  const loadMindMapFromData = useCallback((data, options = {}) => {
    const { preserveSelectedId = false } = options;
    try {
      const parsedData = typeof data === 'string' ? JSON.parse(data) : data;

      if (!isValidData(parsedData)) {
        throw new Error('Invalid mind map data structure');
      }

      const restored = restoreLegacyCollapsedMap(
        Array.isArray(parsedData.nodes) ? parsedData.nodes : [],
        Array.isArray(parsedData.relationships) ? parsedData.relationships : []
      );
      const nodesArrayRaw = restored.nodes;
      const relationshipsArray = restored.relationships;

      // Normalize node positions so every node has a valid numeric (x, y).
      const nodesArray = normalizeNodes(nodesArrayRaw, null);

      setNodes(nodesArray);
      setRelationships(relationshipsArray);
      setRoot(parsedData.root || null);
      projectedRelationshipsRef.current = relationshipsArray;

      // For Yjs-backed collaboration we keep selectedId as
      // purely local UI state. Callers can opt out of
      // restoring selectedId from snapshots.
      if (!preserveSelectedId) {
        // Delay selectedId to ensure nodes are rendered first
        setTimeout(() => {
          setSelectedId(parsedData.selectedId || null);
        }, 0);
      }

      rebuildParentMap(parentMap, nodesArray, relationshipsArray);
      console.log("Mind map loaded");
    } catch (err) {
      console.error("Failed to load mind map:", err);
    }
  }, [setNodes, setRelationships, setRoot, setSelectedId, parentMap, isValidData]);

  const projectMindMapFromData = useCallback((data, options = {}) => {
    const { preserveSelectedId = true } = options;
    const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
    if (!isValidData(parsedData)) {
      throw new Error('Invalid mind map projection');
    }
    if (hasLegacyCollapsedContent(parsedData.nodes)) {
      loadMindMapFromData(parsedData, { preserveSelectedId });
      return { mode: 'legacy-full' };
    }

    const nodesArray = normalizeNodes(parsedData.nodes, null);
    const relationshipsArray = parsedData.relationships;
    setNodes(nodesArray);
    setRelationships(relationshipsArray);
    setRoot(parsedData.root || null);

    if (projectedRelationshipsRef.current !== relationshipsArray) {
      projectedRelationshipsRef.current = relationshipsArray;
      rebuildParentMap(parentMap, nodesArray, relationshipsArray);
    }

    if (!preserveSelectedId) {
      setTimeout(() => setSelectedId(parsedData.selectedId || null), 0);
    }
    return { mode: 'incremental' };
  }, [
    isValidData,
    loadMindMapFromData,
    parentMap,
    setNodes,
    setRelationships,
    setRoot,
    setSelectedId,
  ]);

  const loadMindMap = useCallback(() => {
    // In the signed-in-only flow, structural data is loaded from the
    // backend (via App -> remoteMapData) and then from Yjs. We no
    // longer hydrate from localStorage. For safety, keep a minimal
    // default so the UI has something to render before Yjs attaches.

    const data = getDefaultData();
    loadMindMapFromData(data);
  }, [getDefaultData, loadMindMapFromData]);

  return {
    saveMindMap,
    loadMindMap,
    loadMindMapFromData,
    projectMindMapFromData,
    serializeMap
  };
};
